"use client";

import { motion } from "motion/react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils";

type Item = { title: string; body: string; icon?: ReactNode; accent?: "red" | "coral" | "purple" | "cyan" | "denim" | "emerald" | "lime" | "amber-gold" | "crimson"; description?: string; tag?: string };

const ACCENT_GRADIENT: Record<NonNullable<Item["accent"]>, string> = {
  red: "from-red-500 to-coral-500", coral: "from-coral-500 to-amber-gold-500", purple: "from-purple-500 to-cyan-500", cyan: "from-cyan-500 to-emerald-500", denim: "from-denim-500 to-purple-500", emerald: "from-emerald-500 to-lime-500", lime: "from-lime-500 to-amber-gold-500", "amber-gold": "from-amber-gold-500 to-coral-500", crimson: "from-crimson-500 to-red-500",
};
const ACCENT_BG: Record<NonNullable<Item["accent"]>, string> = {
  red: "from-red-500/10 to-red-500/0", coral: "from-coral-500/10 to-coral-500/0", purple: "from-purple-500/10 to-purple-500/0", cyan: "from-cyan-500/10 to-cyan-500/0", denim: "from-denim-500/10 to-denim-500/0", emerald: "from-emerald-500/10 to-emerald-500/0", lime: "from-lime-500/10 to-lime-500/0", "amber-gold": "from-amber-gold-500/10 to-amber-gold-500/0", crimson: "from-crimson-500/10 to-crimson-500/0",
};

export function CardGridBlock({ items, columns = 3, title, eyebrow, description, variant = "icon" }: { items: Item[]; columns?: 2 | 3 | 4; title?: string; eyebrow?: string; description?: string; variant?: "icon" | "number" | "tag" }) {
  const colClass = { 2: "grid-cols-1 lg:grid-cols-2", 3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3", 4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" }[columns];
  return (
    <div className="space-y-10">
      {(eyebrow || title || description) && (
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mx-auto max-w-3xl text-center">
          {eyebrow && <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-amber-gold-500">{eyebrow}</div>}
          {title && <h2 className="font-rajdhani text-3xl font-bold text-foreground lg:text-4xl">{title}</h2>}
          {description && <p className="mt-3 text-foreground/80/80">{description}</p>}
        </motion.div>
      )}
      <div className={cn("grid gap-5", colClass)}>
        {items.map((item, i) => {
          const accent = item.accent ?? "purple";
          return (
            <motion.article key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.04 }} className="glass-card relative overflow-hidden rounded-2xl border border-border p-6">
              <div className={cn("absolute inset-0 bg-gradient-to-br", ACCENT_BG[accent])} />
              <div className="relative">
                <div className="mb-4 flex items-start justify-between">
                  {variant === "icon" && item.icon && <div className={cn("flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br text-foreground", ACCENT_GRADIENT[accent])}>{item.icon}</div>}
                  {variant === "number" && <div className={cn("flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br font-rajdhani text-lg font-bold text-foreground", ACCENT_GRADIENT[accent])}>{String(i + 1).padStart(2, "0")}</div>}
                  {variant === "tag" && item.tag && <div className={cn("rounded-full bg-gradient-to-r px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-foreground", ACCENT_GRADIENT[accent])}>{item.tag}</div>}
                </div>
                <h3 className="font-rajdhani text-lg font-bold text-foreground">{item.title}</h3>
                {item.description && <div className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{item.description}</div>}
                <p className="mt-3 text-sm leading-relaxed text-foreground/80/80">{item.body}</p>
              </div>
            </motion.article>
          );
        })}
      </div>
    </div>
  );
}
